import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PostBlob, Post } from '@/api/api-middleware';
import { apiPath } from '@/constants/defaultValues';
import { downloadFile, openFileInNewTab } from '@/helpers/Utils';

const initialState = {
  loading: false,
  DailyEmployeeSummaryPreview: [],
  AttendanceReportData: [],
  MonthlyAttendanceReportData: [],
  EmployeeWorkScheduleReportData: [],
  AbsenteesReportData: [],
  requestReportData: [],
  error: '',
};

export const GetDailyEmployeeSummaryPreview = createAsyncThunk(
  'report/GetDailyEmployeeSummaryPreview',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Reporting/ReportPreview/AttendanceSummary'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const GetAttendanceReport = createAsyncThunk(
  'report/GetAttendanceReport',
  async (params, thunkAPI) => {
    try {
      const url = `${apiPath}${'api/Reporting/AttendanceReport'}`;
      if (params?.responseType !== 3) {
        const response = await PostBlob({
          url: url,
          data: params,
        });

        params?.responseType === 1
          ? openFileInNewTab(response)
          : downloadFile(response);
      } else {
        const response = await Post({
          url: url,
          data: params,
        });
        return response?.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const GetMonthlyAttendanceReport = createAsyncThunk(
  'report/GetMonthlyAttendanceReport',
  async (params, thunkAPI) => {
    try {
      const url = `${apiPath}${'api/Reporting/MonthlyAttendanceReport'}`;
      if (params?.responseType !== 3) {
        const response = await PostBlob({
          url: url,
          data: params,
        });

        params?.responseType === 1
          ? openFileInNewTab(response)
          : downloadFile(response);
      } else {
        const response = await Post({
          url: url,
          data: params,
        });
        return response?.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const GetAnnualAttendanceSummaryReport = createAsyncThunk(
  'report/GetAnnualAttendanceSummaryReport',
  async (params, thunkAPI) => {
    try {
      const url = `${apiPath}${'api/Reporting/AnnualAttendanceSummaryReport'}`;
      if (params?.responseType !== 3) {
        const response = await PostBlob({
          url: url,
          data: params,
        });
        params?.responseType === 1
          ? openFileInNewTab(response)
          : downloadFile(response);
      } else {
        const response = await Post({
          url: url,
          data: params,
        });
        return response?.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const GetEmployeeWorkScheduleReport = createAsyncThunk(
  'report/GetEmployeeWorkScheduleReport',
  async (params, thunkAPI) => {
    try {
      const url = `${apiPath}${'api/Reporting/EmployeeWorkScheduleReport'}`;
      const response = await Post({
        url: url,
        data: params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const GetAbsenteesReport = createAsyncThunk(
  'report/GetAbsenteesReport',
  async (params, thunkAPI) => {
    try {
      const url = `${apiPath}${'api/Reporting/AbsenteesReport'}`;
      if (params?.responseType !== 3) {
        const response = await PostBlob({
          url: url,
          data: params,
        });
        params?.responseType === 1
          ? openFileInNewTab(response)
          : downloadFile(response);
      } else {
        const response = await Post({
          url: url,
          data: params,
        });
        return response?.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const GetEmployeeRequestsReport = createAsyncThunk(
  'report/GetEmployeeRequestsReport',
  async (params, thunkAPI) => {
    try {
      const url = `${apiPath}${'api/Reporting/EmployeeRequestReport'}`;
      if (params?.responseType !== 3) {
        const response = await PostBlob({
          url: url,
          data: params,
        });
        params?.responseType === 1
          ? openFileInNewTab(response)
          : downloadFile(response);
      } else {
        const response = await Post({
          url: url,
          data: params,
        });
        return response?.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const AuditLogReport = createAsyncThunk(
  'report/AuditLogReport',
  async (params, thunkAPI) => {
    try {
      const url = `${apiPath}${'api/Reporting/AuditLogReport'}`;
      if (params?.responseType !== 3) {
        const response = await PostBlob({
          url: url,
          data: params,
        });

        params?.responseType === 1
          ? openFileInNewTab(response)
          : downloadFile(response);
      } else {
        const response = await Post({
          url: url,
          data: params,
        });
        return response?.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.AttendanceReportData = [];
      state.MonthlyAttendanceReportData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetAttendanceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetAttendanceReport.fulfilled, (state, action) => {
        state.loading = false;
        state.AttendanceReportData = action.payload;
        state.error = '';
      })
      .addCase(GetAttendanceReport.rejected, (state, action) => {
        state.loading = false;
        state.AttendanceReportData = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(GetMonthlyAttendanceReport.pending, (state) => {
        state.loading = true;
        state.MonthlyAttendanceReportData = [];
      })
      .addCase(GetMonthlyAttendanceReport.fulfilled, (state, action) => {
        state.loading = false;
        state.MonthlyAttendanceReportData = action.payload;
        state.error = '';
      })
      .addCase(GetMonthlyAttendanceReport.rejected, (state, action) => {
        state.loading = false;
        state.MonthlyAttendanceReportData = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(GetAnnualAttendanceSummaryReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetAnnualAttendanceSummaryReport.fulfilled, (state, action) => {
        state.loading = false;
        state.AttendanceReportData = action.payload;
        state.error = '';
      })
      .addCase(GetAnnualAttendanceSummaryReport.rejected, (state, action) => {
        state.loading = false;
        state.AttendanceReportData = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(GetEmployeeWorkScheduleReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetEmployeeWorkScheduleReport.fulfilled, (state, action) => {
        state.loading = false;
        state.EmployeeWorkScheduleReportData = action.payload;
        state.error = '';
      })
      .addCase(GetEmployeeWorkScheduleReport.rejected, (state, action) => {
        state.loading = false;
        state.EmployeeWorkScheduleReportData = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(GetAbsenteesReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetAbsenteesReport.fulfilled, (state, action) => {
        state.loading = false;
        state.AbsenteesReportData = action.payload;
        state.error = '';
      })
      .addCase(GetAbsenteesReport.rejected, (state, action) => {
        state.loading = false;
        state.AbsenteesReportData = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(GetEmployeeRequestsReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetEmployeeRequestsReport.fulfilled, (state, action) => {
        state.loading = false;
        state.requestReportData = action.payload;
        state.error = '';
      })
      .addCase(GetEmployeeRequestsReport.rejected, (state, action) => {
        state.loading = false;
        state.requestReportData = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(AuditLogReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(AuditLogReport.fulfilled, (state, action) => {
        state.loading = false;
        state.requestReportData = action.payload;
        state.error = '';
      })
      .addCase(AuditLogReport.rejected, (state, action) => {
        state.loading = false;
        state.requestReportData = [];
        state.error = action.payload ?? 'An error occurred';
      });
  },
});
export const { clearCurrentItem } = reportSlice.actions;
export default reportSlice.reducer;
